import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Card, 
  Typography, 
  CircularProgress, 
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  WbSunny,
  Cloud,
  Opacity,
  Air,
  Visibility,
  Schedule,
  NavigateNext,
  NavigateBefore
} from '@mui/icons-material';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'https://tripoptimizebackend.onrender.com';

const WeatherCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '20px',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  }
}));

const WeatherIcon = styled('img')({
  width: '64px',
  height: '64px',
  marginRight: '16px'
});

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '12px',
  background: 'rgba(0, 0, 0, 0.03)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)'
  }
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  background: 'white',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    background: 'white',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  }
}));

const kelvinToCelsius = (kelvin) => (kelvin - 273.15).toFixed(1);
const kelvinToFahrenheit = (kelvin) => ((kelvin - 273.15) * 9/5 + 32).toFixed(1);

const Weather = ({ location, date }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${BACKEND_API_URL}/forecast`, {
          place: location,
          date: date
        });
        setWeatherData(response.data);
      } catch (err) {
        setError('Failed to load weather data');
      } finally {
        setLoading(false);
      }
    };

    if (location) {
      fetchWeather();
    }
  }, [location, date]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !weatherData) {
    return null;
  }

  const forecast = weatherData.list[currentIndex];
  const totalForecasts = weatherData.list.length;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalForecasts);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + totalForecasts) % totalForecasts);
  };

  return (
    <WeatherCard>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <WeatherIcon 
          src={forecast.weather[0].icon} 
          alt={forecast.weather[0].description}
        />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            {kelvinToCelsius(forecast.main.temprature)}°C / {kelvinToFahrenheit(forecast.main.temprature)}°F
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {forecast.weather[0].description}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <InfoItem>
            <Opacity sx={{ color: 'primary.main' }} />
            <Typography variant="body2">
              Humidity: {forecast.main.humidity}{forecast.main.humidity_unit}
            </Typography>
          </InfoItem>
        </Grid>
        <Grid item xs={6}>
          <InfoItem>
            <Air sx={{ color: 'primary.main' }} />
            <Typography variant="body2">
              Wind: {forecast.wind.speed} {forecast.wind.speed_unit}
            </Typography>
          </InfoItem>
        </Grid>
        <Grid item xs={6}>
          <InfoItem>
            <Visibility sx={{ color: 'primary.main' }} />
            <Typography variant="body2">
              Visibility: {(forecast.visibility_distance / 1000).toFixed(1)} km
            </Typography>
          </InfoItem>
        </Grid>
        <Grid item xs={6}>
          <InfoItem>
            <Schedule sx={{ color: 'primary.main' }} />
            <Typography variant="body2">
              {new Date(forecast.dt_txt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </InfoItem>
        </Grid>
      </Grid>

      {totalForecasts > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Tooltip title="Previous forecast">
            <NavigationButton onClick={handlePrevious} size="small">
              <NavigateBefore />
            </NavigationButton>
          </Tooltip>
          <Tooltip title="Next forecast">
            <NavigationButton onClick={handleNext} size="small">
              <NavigateNext />
            </NavigationButton>
          </Tooltip>
        </Box>
      )}
    </WeatherCard>
  );
};

export default Weather;
